/**
 * API client functions for department-based access management.
 *
 * Mirrors `accessManagerApi.js`: every call goes through the shared apiClient
 * (auth headers, HMAC signing, retry policy) with a path resolved from the
 * central endpoint table, and no function catches — errors propagate to the
 * caller for normalization by `normalizeApiError` / `formatErrorMessage`
 * (Req 1.2, 1.5).
 *
 * Like `accessManagerApi.js`, every request function resolves with the raw
 * axios response, so callers read `response.data` themselves. The two
 * exported normalizers below take that `response.data` payload and absorb
 * wrapper-shape variation at the boundary, so no component branches on
 * response shape.
 */

import apiClient from "../../utils/axiosConfig";
import { getApiUrl, API_ENDPOINTS } from "../../Configurations/config";

const DEPARTMENTS = API_ENDPOINTS.ADMIN.DEPARTMENTS;

/**
 * Fetches every department.
 *
 * GET /api/admin/departments
 *
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function listDepartments() {
  return apiClient.get(getApiUrl(DEPARTMENTS.LIST));
}

/**
 * Creates a department.
 *
 * POST /api/admin/departments
 *
 * @param {Object} department
 * @param {string} department.name - Department name (already trimmed by the caller).
 * @param {string} [department.description] - Human-readable description.
 * @param {string[]} department.scopes - The scopes this department grants.
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function createDepartment({ name, description, scopes }) {
  return apiClient.post(getApiUrl(DEPARTMENTS.CREATE), { name, description, scopes });
}

/**
 * Updates an existing department.
 *
 * PUT /api/admin/departments/{id}
 *
 * @param {number|string} id - The department id.
 * @param {Object} department
 * @param {string} department.name - Department name (already trimmed by the caller).
 * @param {string} [department.description] - Human-readable description.
 * @param {string[]} department.scopes - The scopes this department grants.
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function updateDepartment(id, { name, description, scopes }) {
  return apiClient.put(getApiUrl(DEPARTMENTS.DETAIL(id)), { name, description, scopes });
}

/**
 * Soft-deletes (deactivates) a department.
 *
 * DELETE /api/admin/departments/{id}
 *
 * @param {number|string} id - The department id.
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function deleteDepartment(id) {
  return apiClient.delete(getApiUrl(DEPARTMENTS.DETAIL(id)));
}

/**
 * Fetches the members of one department.
 *
 * GET /api/admin/departments/{id}/members
 *
 * @param {number|string} id - The department id.
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function listDepartmentMembers(id) {
  return apiClient.get(getApiUrl(DEPARTMENTS.MEMBERS(id)));
}

/**
 * Adds a user to a department.
 *
 * POST /api/admin/departments/assign
 *
 * @param {string} email - The user's email address.
 * @param {number|string} departmentId - The department id.
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function assignUserToDepartment(email, departmentId) {
  return apiClient.post(getApiUrl(DEPARTMENTS.ASSIGN), { email, department_id: departmentId });
}

/**
 * Removes a user from a department.
 *
 * POST /api/admin/departments/remove
 *
 * @param {string} email - The user's email address.
 * @param {number|string} departmentId - The department id.
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function removeUserFromDepartment(email, departmentId) {
  return apiClient.post(getApiUrl(DEPARTMENTS.REMOVE), { email, department_id: departmentId });
}

/**
 * Fetches the departments one user belongs to.
 *
 * GET /api/admin/departments/user/{email}
 *
 * The email path segment is encoded here rather than in the config table, so
 * the config stays a plain path table and the encoding is testable next to
 * the call (Req 1.3).
 *
 * @param {string} email - The user's email address.
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export function getUserDepartments(email) {
  return apiClient.get(getApiUrl(DEPARTMENTS.USER(encodeURIComponent(email))));
}

/**
 * Normalizes a department list response into an array of departments.
 * Tolerates a bare array or any of the known object wrappers, following the
 * `extractEvents` precedent in `AuditLogDrawer`. Anything unrecognised
 * resolves to an empty array so the UI degrades to an empty state rather
 * than throwing during render.
 *
 * @param {*} data - The `response.data` payload.
 * @returns {Array<Object>}
 */
export function extractDepartments(data) {
  if (Array.isArray(data)) return data;

  const wrapped = data?.departments ?? data?.data;
  return Array.isArray(wrapped) ? wrapped : [];
}

/**
 * Normalizes a member list response into an array of email strings.
 * Tolerates a bare array of emails, an array of member objects carrying an
 * `email` field, and the `{ members }` / `{ data }` wrappers. Entries that
 * yield no usable email are dropped, and anything unrecognised resolves to
 * an empty array.
 *
 * @param {*} data - The `response.data` payload.
 * @returns {string[]}
 */
export function extractMemberEmails(data) {
  let list;
  if (Array.isArray(data)) {
    list = data;
  } else {
    const wrapped = data?.members ?? data?.data;
    list = Array.isArray(wrapped) ? wrapped : [];
  }

  return list.reduce((emails, member) => {
    if (typeof member === "string") {
      emails.push(member);
    } else if (typeof member?.email === "string") {
      emails.push(member.email);
    }
    return emails;
  }, []);
}
